// tenancy: resellers, accounts, locations (catalog-outline.md "tenancy"). Phase 0 seed: set a location's tax rate.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { secret } from '../../define/secret.ts';
import { ConfigFieldsWritten } from '../config/index.ts';
import { AccountId, IsoInstant, LocationId, Rate } from '../../primitives/codecs.ts';

export const LocationNotFound = defineError({
  code: 'tenancy.location_not_found', httpStatus: 404, message: 'errors.tenancy.location_not_found', retryable: false,
  details: z.object({ locationId: LocationId }),
});

export const TaxRateChanged = defineEvent({
  name: 'TaxRateChanged',
  version: 1,
  summary: "A location's default tax rate changed.",
  aggregate: 'config',
  payload: z.object({ locationId: LocationId, taxRate: Rate, previousTaxRate: Rate, changedAt: IsoInstant }),
  verifiable: ['taxRate'],
  merge: 'lifecycle',
  deviceConsumed: true,
});

export const SetTaxRate = defineCommand({
  name: 'tenancy.setTaxRate',
  summary: "Set a location's default tax rate (parts per 10^8).",
  input: z.object({ locationId: LocationId, taxRate: Rate }),
  output: z.object({ locationId: LocationId, taxRate: Rate, previousTaxRate: Rate }),
  permission: 'settings.location.manage',
  risk: 'high',
  availability: 'location',
  origin: 'any',
  emits: [TaxRateChanged],
  errors: [LocationNotFound],
  dryRun: true,
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['PATCH /v1/locations/{id}'],
});

/** A location as the shell shows it: the switcher's name, the URL slug, and the zone every time on its pages is read in. */
export const LocationSummary = z
  .object({
    locationId: LocationId,
    name: z.string(),
    slug: z.string(),
    /** IANA zone: reports, timecards and discount schedules are on this clock ("Times in location time (…)"). */
    timezone: z.string(),
    currency: z.enum(['USD', 'CAD']),
    /** Location Info's address: the location switcher shows the city on its button and the address on each card. */
    address: z.object({ line1: z.string(), city: z.string(), state: z.string(), zip: z.string() }),
    /** Location Info's colour (`location.profile.color`): the dot and the initial's avatar in the switcher. */
    color: z.string(),
    /** Location Info's email and phone (`location.profile.email`, `.phone`): the staff Account Detail's Locations table. */
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
  })
  .meta({ id: 'LocationSummary' });

/** The locations the signed-in principal can open (the backoffice location switcher, legacy `GET /v1/auth/locations`). */
export const ListLocations = defineQuery({
  name: 'tenancy.locations.list',
  summary: "The locations the signed-in user can open, with each one's slug, time zone, address and colour.",
  input: z.object({}),
  output: z.object({ locations: z.array(LocationSummary) }),
  permission: { session: 'user' },
  availability: 'location',
  scope: 'account',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/auth/locations'],
});

// ---- brand images (SCR-127; field note backoffice.settings.location.business-profile.branding) -------------------------

/** A location's four brand images: two logos, and the customer display's idle background and cart-side image. */
export const BrandImageSlot = z.enum(['primary_logo', 'small_logomark', 'display_background', 'checkout_screen']).meta({ id: 'BrandImageSlot' });

/** The customer display's two slots (the logos are for online ordering). */
export const DisplayImageSlot = z.enum(['display_background', 'checkout_screen']).meta({ id: 'DisplayImageSlot' });

export const BrandImageType = z.enum(['image/jpeg', 'image/png']).meta({ id: 'BrandImageType' });

/** 5,120 KB (legacy's limit, core `BRAND_IMAGE_MAX_BYTES`) as base64: four characters per three bytes, padded. */
const BRAND_IMAGE_MAX_BASE64 = Math.ceil((5120 * 1024) / 3) * 4;

/** An image's bytes as standard base64 (the command travels as JSON). */
export const Base64Image = z.string().min(4).max(BRAND_IMAGE_MAX_BASE64).regex(/^[A-Za-z0-9+/]*={0,2}$/).meta({ id: 'Base64Image' });

/** The request body cap for a brand image upload: the image's base64 plus the envelope. */
export const BRAND_IMAGE_BODY_BYTES = BRAND_IMAGE_MAX_BASE64 + 64 * 1024;

export const BrandImageInvalid = defineError({
  code: 'tenancy.brand_image_invalid', httpStatus: 422, message: 'errors.tenancy.brand_image_invalid', retryable: false,
  details: z.object({ slot: BrandImageSlot, problem: z.enum(['too_large', 'not_an_image', 'wrong_size']) }),
});

export const SetBrandImage = defineCommand({
  name: 'tenancy.setBrandImage',
  summary: "Set one of a location's brand images (a logo, or the customer display's background or cart-side image), cropped to its slot's size.",
  input: z.object({ locationId: LocationId, slot: BrandImageSlot, contentType: BrandImageType, data: Base64Image }),
  output: z.object({ locationId: LocationId, slot: BrandImageSlot, sha256: z.string(), byteSize: z.int().positive() }),
  permission: 'settings.location.manage',
  risk: 'medium',
  availability: 'cloud',
  cloudReason: 'Brand images are kept in the cloud; displays fetch them from there and keep the last copy for when they are offline.',
  origin: 'any',
  emits: [],
  errors: [LocationNotFound, BrandImageInvalid],
  dryRun: true,
  maxBodyBytes: BRAND_IMAGE_BODY_BYTES,
  agent: { expose: false },
  legacy: ['POST /v1/locations/{id}/branding'],
});

export const BrandImage = z
  .object({ slot: BrandImageSlot, contentType: BrandImageType, sha256: z.string(), byteSize: z.int().positive(), setAt: IsoInstant, data: z.string() })
  .meta({ id: 'BrandImage' });

/** The Branding page: every image the location has, with its bytes for the thumbnails (at most four, each under 5 MB). */
export const ListBrandImages = defineQuery({
  name: 'tenancy.brandImages.list',
  summary: "A location's brand images.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ images: z.array(BrandImage) }),
  permission: 'settings.location.manage',
  availability: 'cloud',
  cloudReason: 'Brand images are kept in the cloud.',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/locations/{id} (branding)'],
});

/**
 * The customer display's images for its location. The display sends the hash of each image it already holds and gets
 * the bytes only of those that changed; a slot with no image comes back absent (the display then shows the Radish logo).
 */
export const DisplayImages = defineQuery({
  name: 'tenancy.displayImages',
  summary: "The customer display's background and cart-side images, sending bytes only for those the display does not hold.",
  input: z.object({ locationId: LocationId, held: z.partialRecord(DisplayImageSlot, z.string()).optional() }),
  output: z.object({
    images: z.array(z.object({ slot: DisplayImageSlot, contentType: BrandImageType, sha256: z.string(), data: z.string().nullable() })),
  }),
  permission: { session: 'device' },
  availability: 'cloud',
  cloudReason: 'Brand images are kept in the cloud; the display keeps the last copy for when it is offline.',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/devices/{id}/status (location.branding)'],
});

// ---- accounts (DLG-128; plan 2026-10-12-accounts-and-locations §2; field note backoffice.accounts.list) ----------------

const ACCOUNTS_CLOUD = 'Accounts are created and edited by Radish staff and resellers in the cloud; no device holds them.';

/** A reseller (legacy `resellers`): Radish admins pick one for an account. */
export const ResellerId = z.uuid().meta({ id: 'ResellerId' });

export const ResellerNotFound = defineError({
  code: 'tenancy.reseller_not_found', httpStatus: 422, message: 'errors.tenancy.reseller_not_found', retryable: false,
  details: z.object({ resellerId: ResellerId }),
});

export const AccountNotFound = defineError({
  code: 'tenancy.account_not_found', httpStatus: 404, message: 'errors.tenancy.account_not_found', retryable: false,
  details: z.object({ accountId: AccountId }),
});

/** An account id already in use by another account (a retried create with the same command id replays instead). */
export const AccountExists = defineError({
  code: 'tenancy.account_exists', httpStatus: 409, message: 'errors.tenancy.account_exists', retryable: false,
  details: z.object({ accountId: AccountId }),
});

/**
 * The account's ACH API key (A39 (b), Andrew 2026-10-12): optional and write-only. It is sealed at rest, never returned
 * or logged, and the dialog shows only its last four characters. Used only by gift-card ACH settlement.
 */
const AchApiKey = secret(z.string().trim().min(4).max(255));

const AccountEmail = z.email().max(255);
const AccountPhone = z.string().trim().max(40);

/** An account as the Create/Edit Account dialog shows it. */
export const AccountView = z
  .object({
    accountId: AccountId,
    resellerId: ResellerId,
    resellerName: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /** The ACH API key's last four characters, or null when none is set. */
    achKeyLastFour: z.string().max(4).nullable(),
  })
  .meta({ id: 'AccountView' });

export const CreateAccount = defineCommand({
  name: 'tenancy.createAccount',
  summary: 'Create an account under a reseller (Radish admins pick the reseller; a reseller always creates under its own).',
  input: z.object({
    /** Minted by the caller (UUIDv7) so a retried create replays. */
    accountId: AccountId,
    /** Required from a Radish admin; a reseller's own reseller is used whatever is sent (legacy AccountController). */
    resellerId: ResellerId.optional(),
    name: z.string().trim().min(1).max(255),
    achApiKey: AchApiKey.optional(),
    email: AccountEmail,
    phone: AccountPhone.optional(),
  }),
  output: AccountView,
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: ACCOUNTS_CLOUD,
  emits: [],
  errors: [ResellerNotFound, AccountExists],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/accounts'],
});

export const UpdateAccount = defineCommand({
  name: 'tenancy.updateAccount',
  summary: "Change an account's name, email, phone or ACH API key (Radish admins also its reseller). A blank ACH key keeps the stored one.",
  input: z.object({
    accountId: AccountId,
    /** Radish admins only; ignored for a reseller. */
    resellerId: ResellerId.optional(),
    name: z.string().trim().min(1).max(255),
    /** Absent keeps the stored key (the field is left blank). */
    achApiKey: AchApiKey.optional(),
    email: AccountEmail,
    phone: AccountPhone.nullable().optional(),
  }),
  output: AccountView,
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: ACCOUNTS_CLOUD,
  emits: [],
  errors: [AccountNotFound, ResellerNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['PUT /v1/accounts/{id}'],
});

export const GetAccount = defineQuery({
  name: 'tenancy.account',
  summary: 'One account as the Edit Account dialog shows it (reseller, contact, and the last four of the ACH key).',
  input: z.object({ accountId: AccountId }),
  output: AccountView,
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: ACCOUNTS_CLOUD,
  scope: 'platform',
  errors: [AccountNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/accounts/{id}'],
});

/** The resellers a Radish admin can pick (a reseller sees only its own). */
export const ListResellers = defineQuery({
  name: 'tenancy.resellers.list',
  summary: 'The resellers the Create/Edit Account dialog offers, by name.',
  input: z.object({}),
  output: z.object({ resellers: z.array(z.object({ resellerId: ResellerId, name: z.string() })) }),
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: ACCOUNTS_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/resellers'],
});

// ---- locations (DLG-129; plan 2026-10-12-accounts-and-locations §3, A40 (b); field note backoffice.accounts.detail) -----

const LOCATIONS_CLOUD = 'Locations are created and edited by Radish staff and resellers in the cloud; registers learn of them through the location feed.';

/** Legacy's five zones (`App\Types\Timezone`, SelectTimezone.vue). */
export const LocationZone = z.enum(['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'Pacific/Honolulu']).meta({ id: 'LocationZone' });

/** A location's ACH bank number (A39 (b)): optional, write-only, sealed; shown by its last four only. */
const BankNumber = secret(z.string().trim().regex(/^[0-9]{4,34}$/));

const Required = z.string().trim().min(1).max(255);

/**
 * A40 (b), Andrew 2026-10-12: one location schema and rule set for both staff pages (and the merchant subset on Location
 * Info): name, email, phone, address, city, state and zip are required, and the staff form's contact names too.
 */
const LocationDetails = {
  name: Required,
  email: z.email().max(255),
  /** Digits only, as legacy's PhoneInput sends them. */
  phone: z.string().trim().regex(/^[0-9]{10,15}$/),
  taxRate: Rate,
  timezone: LocationZone,
  addressLine1: Required,
  city: Required,
  state: Required,
  zip: z.string().trim().min(1).max(32),
  contactFirstName: Required,
  contactLastName: Required,
};

export const LocationView = z
  .object({
    locationId: LocationId,
    accountId: AccountId,
    name: z.string(),
    slug: z.string(),
    email: z.string(),
    phone: z.string(),
    taxRate: Rate,
    timezone: z.string(),
    addressLine1: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    contactFirstName: z.string(),
    contactLastName: z.string(),
    /** The last four digits of the ACH routing and account numbers, or null when none is set. */
    achRoutingLastFour: z.string().max(4).nullable(),
    achAccountLastFour: z.string().max(4).nullable(),
  })
  .meta({ id: 'LocationView' });

export const SlugUnavailable = defineError({
  code: 'tenancy.slug_unavailable', httpStatus: 409, message: 'errors.tenancy.slug_unavailable', retryable: false,
  details: z.object({ slug: z.string() }),
});

export const LocationExists = defineError({
  code: 'tenancy.location_exists', httpStatus: 409, message: 'errors.tenancy.location_exists', retryable: false,
  details: z.object({ locationId: LocationId }),
});

export const CreateLocation = defineCommand({
  name: 'tenancy.createLocation',
  summary: "Create a location in the account: its URL slug from the name (never changed later), time zone, tax rate, address and contact.",
  input: z.object({
    /** Minted by the caller (UUIDv7) so a retried create replays. */
    locationId: LocationId,
    ...LocationDetails,
    achRoutingNumber: BankNumber.optional(),
    achAccountNumber: BankNumber.optional(),
  }),
  output: LocationView,
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LOCATIONS_CLOUD,
  emits: [ConfigFieldsWritten],
  errors: [SlugUnavailable, LocationExists, AccountNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/locations'],
});

export const UpdateLocation = defineCommand({
  name: 'tenancy.updateLocation',
  summary: "Edit a location's name, time zone, tax rate, address and contact (the slug never changes). A blank ACH number keeps the stored one.",
  input: z.object({
    locationId: LocationId,
    ...LocationDetails,
    achRoutingNumber: BankNumber.optional(),
    achAccountNumber: BankNumber.optional(),
  }),
  output: LocationView,
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LOCATIONS_CLOUD,
  emits: [ConfigFieldsWritten],
  errors: [LocationNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['PUT /v1/locations/{id}'],
});

/** Where a location is in its life that a register must know (`tenancy.locations.lifecycle_state`). */
export const LocationLifecycle = z.enum(['active', 'training']).meta({ id: 'LocationLifecycle' });

/**
 * A location becomes a training location (the migration's training tenant, DECIDE-MIG-5) or live again (lane C round
 * 9). One transaction changes `tenancy.locations.lifecycle_state` and writes the settings fact `location.profile.training`
 * (true exactly when 'training'), which is how the location's registers learn it: they show the training bar, offline
 * too. A training location, like a demo one, sends no customer mail and is never billed. Radish staff only. Setting the
 * state it already has writes nothing new. Staging's demo stores ('demo') are not this command's and show no bar.
 */
export const SetLocationLifecycle = defineCommand({
  name: 'tenancy.setLocationLifecycle',
  summary: 'Make a location a training location, or live again; its registers show or drop the training bar.',
  input: z.object({ locationId: LocationId, lifecycleState: LocationLifecycle }),
  output: z.object({ locationId: LocationId, lifecycleState: LocationLifecycle, previous: LocationLifecycle }),
  permission: 'tenancy.lifecycle.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LOCATIONS_CLOUD,
  emits: [ConfigFieldsWritten],
  errors: [LocationNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: [],
});

export const GetLocation = defineQuery({
  name: 'tenancy.location',
  summary: 'One location as the staff Add/Edit Location dialog shows it (the ACH numbers by their last four only).',
  input: z.object({ locationId: LocationId }),
  output: LocationView,
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: LOCATIONS_CLOUD,
  scope: 'account',
  errors: [LocationNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/locations/{id}'],
});

// ---- Remove Location Data (DLG-132; plan 2026-10-13-remove-location-data, A61) ------------------------------------------

export const ClearLocationData = defineCommand({
  name: 'tenancy.clearLocationData',
  summary:
    "Remove a location's menu, discounts and settings data: every live category, department, dining option, time segment, tender type, void reason, "
    + 'tip source, tax exemption, modifier set, item (with its variants) and discount the location owns, each through its own delete. Not reversible.',
  input: z.object({ locationId: LocationId }),
  output: z.object({ locationId: LocationId, cleared: z.literal(true) }),
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LOCATIONS_CLOUD,
  emits: [],
  errors: [LocationNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['GET /v1/delete-menu/{id}'],
});

export const ListLocationsWithItems = defineQuery({
  name: 'tenancy.locations.withItems',
  summary:
    'The locations of the account with a live item Remove Location Data would take (the staff location menu offers it only there, legacy `!requires_data`), '
    + 'and those whose Remove Location Data is still running (the row says so until every delete is done).',
  input: z.object({}),
  output: z.object({ locationIds: z.array(LocationId), clearing: z.array(LocationId) }),
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: LOCATIONS_CLOUD,
  scope: 'account',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/accounts/{id}'],
});

/** What Remove Location Data leaves at a location of a multi-location account: the live account-shared entities. */
export const LocationDataKept = z
  .object({ items: z.int().nonnegative(), variants: z.int().nonnegative(), categories: z.int().nonnegative(), modifierSets: z.int().nonnegative(), discounts: z.int().nonnegative(), departments: z.int().nonnegative() })
  .meta({ id: 'LocationDataKept' });

export const PreviewLocationDataClear = defineQuery({
  name: 'tenancy.locationData.preview',
  summary:
    "What Remove Location Data would keep at a location: the account-shared entities other locations also sell (every department is one), counted so the "
    + "confirm can say so before Proceed. All zero when the location is its account's only open one (then they go too).",
  input: z.object({ locationId: LocationId }),
  output: z.object({ kept: LocationDataKept }),
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: LOCATIONS_CLOUD,
  scope: 'account',
  errors: [LocationNotFound],
  agent: { expose: false },
});
